import React, { useRef, useEffect } from "react";
import { Search, X, LayoutGrid, Rows3, Bookmark, Share2, RefreshCw } from "lucide-react";
import { SOURCE_META, THEME_META, formatRelative } from "../../lib/catalog";

export default function Controls({
  taxonomy, query, setQuery, theme, setTheme, source, setSource,
  lang, setLang, savedOnly, setSavedOnly, savedCount, onShare, sync,
  view, setView, resultCount, searchRef,
}) {
  const themes = taxonomy?.themes || [];
  const sources = taxonomy?.sources || [];
  const langs = taxonomy?.langs || {};
  const lastSync = formatRelative(sync?.last_run);

  return (
    <div id="index" className="scroll-mt-20 max-w-[1320px] mx-auto px-5 sm:px-8 pt-6 pb-2">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6 border-b border-[var(--line)] pb-4">
        <div>
          <p className="meta-label text-[var(--gold)]">The Index</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2 text-[var(--txt)]">All Dossiers</h2>
          {lastSync && (
            <p data-testid="sync-status" className="meta-label text-[var(--muted)] text-[0.56rem] mt-2 flex items-center gap-1.5">
              <RefreshCw size={10} className="text-[var(--gold)]" />
              Auto-synced from Substack · Medium · X — last update {lastSync}
            </p>
          )}
        </div>
        <span data-testid="result-count" className="meta-label text-[var(--muted)]">
          {resultCount} {resultCount === 1 ? "entry" : "entries"}
        </span>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input
          ref={searchRef}
          data-testid="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search titles and summaries…"
          className="w-full bg-[var(--surface)] border border-[var(--line)] focus:border-[var(--gold)] outline-none pl-11 pr-11 py-3.5 text-sm text-[var(--txt)] placeholder:text-[var(--muted)] transition-colors font-sans"
        />
        {query && (
          <button data-testid="clear-search" onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--gold)]">
            <X size={17} />
          </button>
        )}
      </div>

      {/* Theme tabs */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-3">
        <ThemePill active={theme === "all"} onClick={() => setTheme("all")}
          testid="theme-all" label="All Disciplines" count={taxonomy?.total} />
        {themes.map((t) => (
          <ThemePill key={t.id} active={theme === t.id} onClick={() => setTheme(t.id)}
            testid={`theme-${t.id}`} label={t.label} count={t.count} code={t.code} color={THEME_META[t.id]?.color} />
        ))}
      </div>

      {/* Source filters + view toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3 mt-1">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          <span className="meta-label text-[var(--muted)] text-[0.58rem] shrink-0 mr-1">Source /</span>
          <SrcPill active={source === "all"} onClick={() => setSource("all")} testid="source-all" label="All" />
          {sources.map((s) => {
            const m = SOURCE_META[s.id];
            return (
              <SrcPill key={s.id} active={source === s.id} onClick={() => setSource(s.id)}
                testid={`source-${s.id}`} label={`${m?.label || s.label} · ${s.count}`}
                color={active2(source === s.id, m?.text)} />
            );
          })}
        </div>
        <div className="flex items-center border border-[var(--line)]">
          <ViewBtn active={view === "grid"} onClick={() => setView("grid")} testid="view-grid"><LayoutGrid size={15} /></ViewBtn>
          <ViewBtn active={view === "table"} onClick={() => setView("table")} testid="view-table"><Rows3 size={15} /></ViewBtn>
        </div>
      </div>

      {/* Language toggle + reading list */}
      <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
        <div className="flex items-center flex-wrap gap-2">
          <span className="meta-label text-[var(--muted)] text-[0.58rem] shrink-0 mr-1">Language /</span>
          <SrcPill active={lang === "all"} onClick={() => setLang("all")} testid="lang-all" label={`All ${(langs.EN || 0) + (langs.IT || 0)}`} />
          <SrcPill active={lang === "EN"} onClick={() => setLang("EN")} testid="lang-en" label={`English ${langs.EN || 0}`} />
          <SrcPill active={lang === "IT"} onClick={() => setLang("IT")} testid="lang-it" label={`Italiano ${langs.IT || 0}`} color={lang === "IT" ? "var(--gold)" : undefined} />
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reading-list-toggle"
            onClick={() => setSavedOnly(!savedOnly)}
            className="flex items-center gap-2 meta-label text-[0.58rem] px-3.5 py-2 border transition-all"
            style={{
              borderColor: savedOnly ? "var(--gold)" : "var(--line)",
              color: savedOnly ? "var(--gold)" : "var(--txt-2)",
              backgroundColor: savedOnly ? "color-mix(in srgb, var(--gold) 10%, transparent)" : "transparent",
            }}
          >
            <Bookmark size={13} fill={savedOnly ? "currentColor" : "none"} />
            Reading List {savedCount > 0 && <span className="opacity-70">· {savedCount}</span>}
          </button>
          {savedCount > 0 && (
            <button
              data-testid="share-reading-list"
              onClick={onShare}
              className="flex items-center gap-2 meta-label text-[0.58rem] px-3.5 py-2 border border-[var(--line)] text-[var(--txt-2)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-all"
            >
              <Share2 size={13} /> Share
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const active2 = (on, c) => (on ? c : undefined);

function ThemePill({ active, onClick, label, count, code, color, testid }) {
  const c = color || "var(--gold)";
  return (
    <button data-testid={testid} onClick={onClick}
      className="shrink-0 flex items-center gap-2 px-3.5 py-2 border transition-all duration-200"
      style={{
        borderColor: active ? c : "var(--line)",
        backgroundColor: active ? `color-mix(in srgb, ${c} 12%, transparent)` : "transparent",
        color: active ? c : "var(--txt-2)",
      }}>
      {color && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />}
      {code && <span className="meta-label text-[0.52rem] opacity-70">{code}</span>}
      <span className="text-[0.8rem] font-medium whitespace-nowrap">{label}</span>
      {typeof count === "number" && (
        <span className="meta-label text-[0.56rem] opacity-60">{count}</span>
      )}
    </button>
  );
}

function SrcPill({ active, onClick, label, color, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="shrink-0 meta-label text-[0.58rem] px-3 py-1.5 border transition-all whitespace-nowrap"
      style={{
        borderColor: active ? (color || "var(--gold)") : "var(--line)",
        color: active ? (color || "var(--gold)") : "var(--muted)",
        backgroundColor: active ? "rgba(var(--txt-rgb),0.04)" : "transparent",
      }}>
      {label}
    </button>
  );
}

function ViewBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="grid place-items-center w-9 h-9 transition-colors"
      style={{ backgroundColor: active ? "var(--gold)" : "transparent", color: active ? "var(--ink)" : "var(--muted)" }}>
      {children}
    </button>
  );
}
